import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "./hooks";
import {
    fetchLanguages,
    selectLanguageLabels,
    selectLanguages,
    selectLanguageStatus,
} from "../redux/language/language.slice";
import {
    ASSESSMENT_LANGUAGE_LABELS,
    labelForLanguage,
} from "../utils/assessmentLanguage";

/**
 * The language registry for pickers and labels. Asks for it once, the first
 * time any caller finds it idle; until it answers (or if it fails) `labels`
 * and `codes` are the static launch languages, so a picker is never empty.
 *
 * `labels`, `labelFor` and `codes` keep their identity until the registry
 * changes, so they are safe in dependency arrays.
 */
export function useLanguageRegistry() {
    const dispatch = useAppDispatch();
    const languages = useAppSelector(selectLanguages);
    const labels = useAppSelector(selectLanguageLabels);
    const status = useAppSelector(selectLanguageStatus);

    useEffect(() => {
        if (status === "idle") dispatch(fetchLanguages());
    }, [status, dispatch]);

    const codes = useMemo(
        () =>
            languages.length
                ? languages.map((language) => language.code)
                : Object.keys(ASSESSMENT_LANGUAGE_LABELS),
        [languages]
    );

    const labelFor = useCallback(
        (code: string) => labelForLanguage(code, labels),
        [labels]
    );

    return useMemo(
        () => ({ languages, labels, labelFor, codes }),
        [languages, labels, labelFor, codes]
    );
}
